import { useMemo } from "react"

import {
  DEMAND_GROUPING_LEVELS,
  DEMAND_SALES_PLANNING_LEVELS,
  type DemandSalesPlanningLevel,
  extraDataFieldOf,
  isExtraDataLevel,
  isSalesPlanningLevel,
  levelLabel,
  parseDemandHierarchy,
} from "~/utils/demandHierarchy"
import { useExtraDataDisplayNameMap } from "~/hooks/useSku"
import { api } from "~/utils/api"
import { ExtraDataTable, getExtraDataDisplayName } from "~/utils/dynamicTableUtils"

// The vocabulary lives in ~/utils/demandHierarchy (no React) so the server can read it too.
export {
  DEMAND_GROUPING_LEVELS,
  DEMAND_SALES_PLANNING_LEVELS,
  type DemandSalesPlanningLevel,
  isSalesPlanningLevel,
  levelLabel,
}

/**
 * How to NAME a level on screen; use this, not `levelLabel`, which cannot resolve an extraData
 * level's `userSetDisplayName`. The map comes from the `ExtraFieldMetadata` read, not the slow
 * hierarchy insights. Falls back to the cased field name.
 */
export function useLevelLabel(): (level: string) => string {
  const { data: displayNames } = useExtraDataDisplayNameMap()

  return useMemo(
    () => (level: string) =>
      isExtraDataLevel(level)
        ? getExtraDataDisplayName(extraDataFieldOf(level), ExtraDataTable.SKU, displayNames)
        : levelLabel(level),
    [displayNames],
  )
}

// The configured, ordered level keys (mirrors budgetHierarchies). Empty = the variant total.
export function useDemandHierarchy() {
  const { data: company } = api.company.getCompany.useQuery()

  const levels = useMemo<string[]>(
    () => parseDemandHierarchy(company?.demandHierarchies),
    [company?.demandHierarchies],
  )

  return useMemo(
    () => ({
      levels,
      count: levels.length,
      firstHierarchy: levels[0],
      getHierarchyAt: (i: number) => levels[i],
      getNextHierarchy: (currentLevel: number) => levels[currentLevel + 1],
      canDrillDown: (currentLevel: number) => currentLevel < levels.length - 1,
    }),
    [levels],
  )
}
